import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Icons } from '@solvhealth/jigsaw-v1';
import { usePalette } from '~/hooks/usePalette';
import { colors } from '../../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { VisitModalities } from '~/shapes/Slot';

type Props = {
  isSelected?: boolean;
  isAvailable?: boolean;
  className?: string;
  darkTheme?: boolean;
  onSelect?: (...args: any[]) => any;
  isSrp?: boolean;
  time?: string | any;
  isCTA?: boolean;
  modality?: VisitModalities;
  showModalitySubtitle?: boolean;
  palette?: {
    base: string;
    ['100']: string;
    ['200']: string;
    ['500']: string;
  };
};

const Container = styled.div.withConfig({ componentId: 'sc-ofesm-0' })<PropsWithChildren<Props>>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: ${colors.theWhiteAlbum};
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.small};
  letter-spacing: -0.13px;
  line-height: 16px;
  text-align: center;
  width: 74px;
  padding: 7px 0;
  cursor: pointer;
  box-sizing: border-box;
  transition: color 200ms ease, background-color 200ms ease;
  border-radius: 4px;
  ${(props) => css`
    color: ${props.palette?.base || colors.crunchBerry};
    border: 1px solid ${props.palette?.['200'] || colors.crunchBerryLight};
  `}
  ${(props) =>
    props.darkTheme &&
    css`
      border-color: #dee6eb;
      color: ${colors.myRightFoot};
    `}
  ${(props) =>
    props.isSelected &&
    !props.isCTA &&
    css`
      background-color: ${props.palette?.['100'] || colors.crunchBerryXLight};
      color: ${props.palette?.['500'] || colors.crunchBerryDark};
      border: 1px solid ${props.palette?.['500'] || colors.crunchBerryDark};
    `}
  ${(props) =>
    !props.isAvailable &&
    !props.isSelected &&
    !props.isCTA &&
    css`
      border: 1px solid ${colors.gray100};
      color: ${colors.gray300};
      cursor: default;
      background-color: ${colors.theWhiteAlbum};
    `}
  ${(props) =>
    props.isAvailable &&
    !props.isSelected &&
    !props.isCTA &&
    css`
      &:hover {
        background-color: ${colors.theWhiteAlbum};
        color: ${props.palette?.base || colors.crunchBerry};
        border: 1px solid ${props.palette?.base || colors.crunchBerry};
      }
    `}
  ${(props) =>
    props.isCTA &&
    css`
      background-color: ${props.palette?.['100'] || colors.crunchBerryXLight};
      color: ${props.palette?.['500'] || colors.crunchBerryDark};
      border: 1px solid ${props.palette?.['100'] || colors.crunchBerryXLight};
    `}
`;

const ModalitySubtitle = styled.div.withConfig({ componentId: 'sc-ofesm-1' })`
  display: flex;
  justify-content: center;
  font-size: 11px;
  line-height: 12px;
  font-family: ${fontFamily.book};
  text-transform: uppercase;
`;

const SrpContainer = styled(Container).withConfig({ componentId: 'sc-ofesm-2' })`
  background-color: ${colors.crunchBerry};
  color: ${colors.theWhiteAlbum};
  border: 1px solid ${colors.crunchBerry};

  ${(props) =>
    props.isSelected &&
    css`
      background-color: ${colors.theWhiteAlbum};
      color: ${colors.crunchBerry};
    `}

  ${(props) =>
    props.isAvailable &&
    !props.isSelected &&
    css`
      &:hover {
        background-color: ${colors.theWhiteAlbum};
        color: ${colors.crunchBerry};
      }
    `}
`;

const TimeSlot = (props: Props) => {
  const {
    isSelected,
    isAvailable,
    time,
    className,
    darkTheme,
    onSelect,
    isSrp = false,
    isCTA = false,
    modality,
    showModalitySubtitle = false,
  } = props;

  const { palette } = usePalette();

  const Subtitle = React.useMemo(() => {
    if (modality === 'telemed') {
      return (
        <ModalitySubtitle>
          <Icons.VideoCamera pr={0.5} size={10} /> Video
        </ModalitySubtitle>
      );
    }
    if (modality === 'in-person') {
      return <ModalitySubtitle>In Office</ModalitySubtitle>;
    }
    return null;
  }, [modality]);

  const SlotContainer = isSrp ? SrpContainer : Container;
  return (
    <SlotContainer
      className={className}
      darkTheme={darkTheme}
      data-testid="timeSlot"
      isAvailable={isAvailable}
      isCTA={isCTA}
      isSelected={isSelected}
      onClick={onSelect}
      palette={palette}
    >
      <span>{time}</span>
      {showModalitySubtitle && <>{Subtitle}</>}
    </SlotContainer>
  );
};

export default TimeSlot;
